import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2, MapPin, Users, Clock, CalendarRange } from "lucide-react";
import { DayPicker } from "react-day-picker";
import Reveal from "./Reveal";
import {
  api,
  formatApiErrorDetail,
  parseCohortDate,
  toISODate,
  formatLongDate,
  formatShortDate,
  timeRange,
} from "../../lib/api";

const EMPTY = { name: "", email: "", phone: "", goal: "" };

export default function ApplyForm() {
  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [location, setLocation] = useState(null);
  const [selected, setSelected] = useState(null); // program object
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const fetchPrograms = async () => {
    try {
      const { data } = await api.get("/programs");
      setPrograms(data);
      const locs = [...new Set(data.map((p) => p.location))];
      setLocation((prev) => prev || locs[0] || null);
    } catch (e) {
      /* silent */
    } finally {
      setLoadingPrograms(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  const locations = useMemo(
    () => [...new Set(programs.map((p) => p.location))],
    [programs]
  );

  const locationPrograms = useMemo(
    () => programs.filter((p) => p.location === location),
    [programs, location]
  );

  const startDateMap = useMemo(() => {
    const m = {};
    locationPrograms.forEach((p) => {
      if (p.start_date) m[p.start_date] = p;
    });
    return m;
  }, [locationPrograms]);

  const startDates = locationPrograms
    .filter((p) => p.start_date)
    .map((p) => parseCohortDate(p.start_date));

  const sessionDates = selected
    ? selected.sessions.map((s) => parseCohortDate(s))
    : [];

  const isStartDate = (day) =>
    startDates.some((d) => toISODate(d) === toISODate(day));

  const onSelectDay = (day) => {
    if (!day) return;
    const prog = startDateMap[toISODate(day)];
    if (prog) {
      setSelected(prog);
      setResult(null);
    }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.error("Please choose a location and boot camp first.");
      return;
    }
    if (!form.name || !form.email || !form.phone || !form.goal) {
      toast.error("Please fill in every field to apply.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        program_id: selected.id,
        ...form,
      });
      setResult(data);
      setForm(EMPTY);
      toast.success(
        data.status === "waitlist"
          ? "You're on the waitlist — we'll reach out if a spot opens."
          : "Spot reserved! Tristian will be in touch shortly."
      );
      fetchPrograms();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setResult(null);
    setSelected(null);
    fetchPrograms();
  };

  return (
    <section
      id="apply"
      data-testid="apply-section"
      className="relative py-28 sm:py-40 px-5 sm:px-8 bg-[#0a0a0a] border-t border-white/5"
    >
      <div className="max-w-[1200px] mx-auto">
        <Reveal>
          <div className="mb-14 max-w-2xl">
            <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#d4af37] mb-6">
              Fifteen Sessions. One Decision.
            </p>
            <h2 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.85]">
              Reserve your <span className="gold-text">spot</span>
            </h2>
            <p className="mt-6 text-lg font-light text-neutral-300 leading-relaxed">
              Every boot camp is <span className="text-white font-medium">15 coached sessions</span> and
              capped at <span className="text-white font-medium">6 people</span>. Pick your gym and time
              slot — the calendar shows every session day.
            </p>
          </div>
        </Reveal>

        {result ? (
          <Reveal>
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="apply-success"
              className="border border-[#d4af37]/40 bg-[#111111] p-12 max-w-2xl"
            >
              <CheckCircle2 className="w-14 h-14 text-[#d4af37] mb-6" strokeWidth={1.5} />
              <h3 className="font-display font-bold text-3xl mb-3">
                {result.status === "waitlist" ? "You're on the waitlist." : "You're in."}
              </h3>
              <p className="text-neutral-300 font-light leading-relaxed mb-6">
                {result.status === "waitlist"
                  ? `This boot camp is currently full. You're #${result.position} on the waitlist for ${result.location} (${result.time_range}), starting ${formatLongDate(result.start_date)}. We'll email you the moment a spot opens.`
                  : `Your spot is reserved for ${result.location} (${result.time_range}), starting ${formatLongDate(result.start_date)}. Check your inbox for the full 15-session schedule — Coach Tristian will confirm the details.`}
              </p>
              <button
                data-testid="apply-again-btn"
                onClick={resetAll}
                className="text-sm text-[#d4af37] underline underline-offset-4"
              >
                Book another spot
              </button>
            </motion.div>
          </Reveal>
        ) : (
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left: location + program + calendar */}
            <Reveal>
              <div className="space-y-8">
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-neutral-500 mb-3">
                    1 · Choose your location
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {locations.length === 0 && !loadingPrograms && (
                      <p className="text-neutral-500 text-sm">
                        No boot camps are open right now. Check back soon.
                      </p>
                    )}
                    {locations.map((loc) => (
                      <button
                        key={loc}
                        type="button"
                        data-testid={`location-btn-${loc.includes("South") ? "south" : "hollis"}`}
                        onClick={() => {
                          setLocation(loc);
                          setSelected(null);
                        }}
                        className={`flex items-center gap-2 px-4 py-3 border text-sm font-medium transition-all duration-300 ${
                          location === loc
                            ? "border-[#d4af37] bg-[#d4af37]/10 text-white"
                            : "border-white/15 text-neutral-400 hover:border-white/40"
                        }`}
                      >
                        <MapPin className="w-4 h-4 text-[#d4af37]" />
                        {loc}
                      </button>
                    ))}
                  </div>
                </div>

                {locationPrograms.length > 0 && (
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase text-neutral-500 mb-3">
                      2 · Choose your boot camp
                    </p>
                    <div className="flex flex-col gap-3" data-testid="program-options">
                      {locationPrograms.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          data-testid={`program-btn-${p.id}`}
                          onClick={() => {
                            setSelected(p);
                            setResult(null);
                          }}
                          className={`text-left border p-4 transition-all duration-300 ${
                            selected?.id === p.id
                              ? "border-[#d4af37] bg-[#d4af37]/10"
                              : "border-white/15 hover:border-white/40"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="flex items-center gap-2 font-medium text-white">
                              <Clock className="w-4 h-4 text-[#d4af37]" />
                              {timeRange(p.start_time, p.end_time)}
                              {p.title ? (
                                <span className="text-neutral-400 font-normal">· {p.title}</span>
                              ) : null}
                            </span>
                            <span
                              className={`text-xs font-bold px-2 py-1 ${
                                p.is_full
                                  ? "bg-amber-500/15 text-amber-400"
                                  : "bg-[#d4af37]/15 text-[#d4af37]"
                              }`}
                            >
                              {p.is_full ? "FULL" : `${p.spots_remaining} LEFT`}
                            </span>
                          </div>
                          <div className="text-sm text-neutral-400 mt-2">
                            {p.session_count} sessions · starts {formatShortDate(p.start_date)}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {locationPrograms.length > 0 && (
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase text-neutral-500 mb-3">
                      3 · Your schedule
                    </p>
                    <div
                      data-testid="booking-calendar"
                      className="inline-block border border-white/10 bg-[#111111] p-4 rounded-md twt-calendar"
                    >
                      {loadingPrograms ? (
                        <div className="p-8 text-neutral-500 text-sm flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                        </div>
                      ) : (
                        <DayPicker
                          mode="single"
                          selected={selected ? parseCohortDate(selected.start_date) : undefined}
                          onSelect={onSelectDay}
                          defaultMonth={
                            selected
                              ? parseCohortDate(selected.start_date)
                              : startDates[0] || undefined
                          }
                          disabled={(day) => !isStartDate(day)}
                          modifiers={{ start: startDates, session: sessionDates }}
                          modifiersClassNames={{
                            start: "twt-available",
                            session: "twt-session",
                          }}
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-5 mt-4 text-xs text-neutral-400">
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-sm bg-[#d4af37]" /> Start date
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-sm bg-[#d4af37]/30 border border-[#d4af37]/50" />{" "}
                        Session day
                      </span>
                    </div>
                    <p className="text-xs text-neutral-500 mt-2">
                      Tip: click a highlighted start date, or pick a boot camp above, to see the full schedule.
                    </p>
                  </div>
                )}

                {selected && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="selected-cohort"
                    className="border border-[#d4af37]/30 bg-[#d4af37]/5 p-5 space-y-3"
                  >
                    <div className="flex items-center gap-2 text-sm text-neutral-300">
                      <Clock className="w-4 h-4 text-[#d4af37]" />
                      {timeRange(selected.start_time, selected.end_time)} · {selected.location}
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CalendarRange className="w-4 h-4 text-[#d4af37]" />
                      <span className="text-neutral-300">
                        {selected.session_count} sessions · starts {formatLongDate(selected.start_date)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="w-4 h-4 text-[#d4af37]" />
                      {selected.is_full ? (
                        <span className="text-amber-400 font-medium">
                          Fully booked — you can join the waitlist.
                        </span>
                      ) : (
                        <span className="text-white font-medium">
                          {selected.spots_remaining} of {selected.capacity} spots remaining
                        </span>
                      )}
                    </div>
                  </motion.div>
                )}
              </div>
            </Reveal>

            {/* Right: form */}
            <Reveal delay={0.15}>
              <form onSubmit={submit} data-testid="apply-form" className="space-y-8 lg:pt-9">
                <p className="text-xs tracking-[0.2em] uppercase text-neutral-500">
                  4 · Your details
                </p>
                <input
                  data-testid="input-name"
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Full Name"
                  className="w-full bg-transparent border-b border-white/20 py-4 text-lg placeholder:text-neutral-600 focus:outline-none focus:border-[#d4af37] transition-colors duration-300"
                />
                <input
                  data-testid="input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="Email Address"
                  className="w-full bg-transparent border-b border-white/20 py-4 text-lg placeholder:text-neutral-600 focus:outline-none focus:border-[#d4af37] transition-colors duration-300"
                />
                <input
                  data-testid="input-phone"
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="Phone Number"
                  className="w-full bg-transparent border-b border-white/20 py-4 text-lg placeholder:text-neutral-600 focus:outline-none focus:border-[#d4af37] transition-colors duration-300"
                />
                <textarea
                  data-testid="input-goal"
                  rows={3}
                  value={form.goal}
                  onChange={update("goal")}
                  placeholder="What is your primary goal for these 15 days?"
                  className="w-full bg-transparent border-b border-white/20 py-4 text-lg placeholder:text-neutral-600 focus:outline-none focus:border-[#d4af37] transition-colors duration-300 resize-none"
                />

                <button
                  type="submit"
                  data-testid="apply-submit-btn"
                  disabled={loading}
                  className="group w-full flex items-center justify-center gap-3 bg-[#d4af37] text-black px-8 py-5 font-bold text-sm tracking-[0.12em] uppercase hover:bg-[#f3e5ab] transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {selected?.is_full
                        ? "Join the Waitlist"
                        : "Join the 15-Day Momentum Boot Camp"}
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        )}
      </div>
    </section>
  );
}
